import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Loader } from "@googlemaps/js-api-loader";

/**
 * LocationAutocomplete — a Google Places city picker for profile edit modals. Client-side geocoding:
 * coordinates and ISO country code are read off the picked place. `onSelect` fires only when the
 * user commits a suggestion (Places' `place_changed`), never on free typing, so a coordinate-less
 * half-typed value can't be submitted. Presentational/uncontrolled: the caller owns value and writes.
 *
 * @param {Object} props
 * @param {string} [props.defaultValue] - The current location string, shown in the input.
 * @param {Function} props.onSelect - Called with { location_string, location_latitude,
 *   location_longitude, location_country_code } when a place is picked.
 * @param {Function} [props.onClear] - When provided, a Remove control blanks the input and calls this.
 * @param {string} [props.id] - Input id, for label association.
 */
function LocationAutocomplete({ defaultValue = "", onSelect, onClear = undefined, id = "location" }) {
  const inputRef = useRef();

  // The place_changed listener is installed once, but callers pass a fresh onSelect closure each
  // render, so the listener reaches the latest one through a ref; the mount-time closure would be stale.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const handleClear = () => {
    if (inputRef.current) inputRef.current.value = "";
    onClear?.();
  };

  // Run-once on mount. Re-running would attach a second Autocomplete and duplicate listeners.
  useEffect(() => {
    let cancelled = false;
    const loader = new Loader({ apiKey: globalThis.Qwoted.GOOGLE_CLIENTSIDE_API_KEY, version: "weekly" });

    const setup = async () => {
      const { Autocomplete } = await loader.importLibrary("places");
      if (cancelled || !inputRef.current) return;

      const autocomplete = new Autocomplete(inputRef.current, {
        types: ["(cities)"],
        fields: ["address_components", "formatted_address", "geometry"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();
        // A place with no geometry can't anchor a map pin or a geo search; treat it as no selection.
        if (lat == null || lng == null) return;

        const country = (place.address_components || []).find((component) => component.types.includes("country"));

        onSelectRef.current({
          location_string: place.formatted_address || inputRef.current.value,
          location_latitude: lat,
          location_longitude: lng,
          location_country_code: country ? country.short_name.toUpperCase() : null,
        });
      });
    };

    setup();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="d-flex align-items-center gap-2">
      <input
        id={id}
        type="text"
        autoComplete="off"
        className="form-control location-autocomplete-input"
        defaultValue={defaultValue}
        ref={inputRef}
      />
      {onClear && (
        <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={handleClear}>
          Remove
        </button>
      )}
    </div>
  );
}

LocationAutocomplete.propTypes = {
  defaultValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  id: PropTypes.string,
};

export default LocationAutocomplete;
